import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { MessageSquare, RefreshCw, Sparkles } from "lucide-react";
import type { InsightSummary } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useAssistantPanel } from "../react/providers/AssistantPanelProvider";

// CockpitAssistant — the cockpit <assistant/> card (form-view revamp Enh 14 ·
// Stage 3D, backed by foundation.ai_insights narration).
//
// Renders the record-scoped AI summary (foundation.ai_insights' grounded
// narration — insights.summarize), with the "Ask" button that opens the
// record-scoped assistant (Enh 05 — the cockpit Ask entry point lives HERE, on
// the card, not as a stray cockpit button) + a Refresh that re-narrates. The
// "Suggested next" nudges live in the separate <suggestions/> card. Omits
// entirely when the record can't be narrated (no decision profile / no summary).

interface Props {
    modelKey: string;
    recordRef: string | null;
}

/** Render a summary string with **bold** spans (the narrator emphasises key
    facts with markdown-style bold). */
function renderBold(text: string): ReactNode[] {
    return text.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
        seg.startsWith("**") && seg.endsWith("**") ? (
            <strong key={i}>{seg.slice(2, -2)}</strong>
        ) : (
            <span key={i}>{seg}</span>
        ),
    );
}

export function CockpitAssistant({ modelKey, recordRef }: Props): ReactNode {
    const runtime = useRuntime();
    const panel = useAssistantPanel();
    const [data, setData] = useState<InsightSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [epoch, setEpoch] = useState(0);

    useEffect(() => {
        if (!recordRef) {
            setLoading(false);
            return;
        }
        let alive = true;
        void (async () => {
            setLoading(true);
            try {
                const res = await runtime.services.insights.summarize(modelKey, recordRef);
                if (alive) setData(res);
            } catch {
                if (alive) setData(null);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [runtime, modelKey, recordRef, epoch]);

    if (!recordRef) return null;
    if (!loading && (!data || !data.available || !data.summary)) return null; // not narratable → omit

    return (
        <div className="cockpit-card cockpit-card--assistant">
            <div className="cockpit-card__head">
                <Sparkles size={13} />
                Assistant
                <button
                    type="button"
                    className="cockpit-asstask"
                    onClick={() => panel.setMode("float")}
                    title="Ask the assistant about this record"
                    data-testid="cockpit-assistant-ask"
                >
                    <MessageSquare size={12} />
                    Ask
                </button>
            </div>
            <div className="cockpit-card__body">
                {loading ? (
                    <p className="cockpit-assistant__loading">Summarizing…</p>
                ) : (
                    <>
                        <p className="cockpit-assistant__summary">{renderBold(data!.summary ?? "")}</p>
                        <div className="cockpit-assistant__foot">
                            <span>{data!.ai ? "Generated by AI · just now" : "Auto summary · just now"}</span>
                            <button
                                type="button"
                                className="cockpit-assistant__refresh"
                                onClick={() => setEpoch((e) => e + 1)}
                            >
                                <RefreshCw size={12} />
                                Refresh
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
